/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import i18n from 'i18next';
import type { UseTranslationResponse } from 'react-i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import { API_NAMESPACE, apiTranslations } from '@opengeoweb/api';
import { AUTH_NAMESPACE, authTranslations } from '@opengeoweb/authentication';
import { CAP_NAMESPACE, capTranslations } from '@opengeoweb/cap';
import { CORE_NAMESPACE, coreTranslations } from '@opengeoweb/core';
import {
  DATA_EXPLORER_NAMESPACE,
  dataExplorerTranslations,
} from '@opengeoweb/data-explorer';
import {
  FORM_FIELDS_NAMESPACE,
  formFieldsTranslations,
} from '@opengeoweb/form-fields';
import {
  KNMICOMPONENTS_NAMESPACE,
  knmiComponentTranslations,
} from '@opengeoweb/knmi-components';
import {
  LAYER_SELECT_NAMESPACE,
  layerSelectTranslations,
} from '@opengeoweb/layer-select';
import { PLUGINS_NAMESPACE, pluginsTranslations } from '@opengeoweb/plugins';
import { SENTRY_NAMESPACE, sentryTranslations } from '@opengeoweb/sentry';
import {
  loadAllTranslations,
  SHARED_NAMESPACE,
  sharedTranslations,
} from '@opengeoweb/shared';
import { SA_NAMESPACE, saTranslations } from '@opengeoweb/sigmet-airmet';
import { SNACKBAR_NAMESPACE, snackbarTranslations } from '@opengeoweb/snackbar';
import {
  SOUNDINGS_NAMESPACE,
  soundingsTranslations,
} from '@opengeoweb/soundings';
import {
  SPACE_WEATHER_NAMESPACE,
  swTranslations,
} from '@opengeoweb/space-weather';
import { TAF_NAMESPACE, tafTranslations } from '@opengeoweb/taf';
import { THEME_NAMESPACE, themeTranslations } from '@opengeoweb/theme';
import {
  TIME_SERIES_NAMESPACE,
  timeseriesTranslations,
} from '@opengeoweb/time-series';
import {
  TIME_SLIDER_NAMESPACE,
  timesliderTranslations,
} from '@opengeoweb/time-slider';
import { WARN_NAMESPACE, warningsTranslations } from '@opengeoweb/warnings';
import {
  WEBMAP_REACT_NAMESPACE,
  webmapReactTranslations,
  WEBMAP_NAMESPACE,
  webmapTranslations,
} from '@opengeoweb/webmap-react';
import {
  WORKSPACE_NAMESPACE,
  workspaceTranslations,
} from '@opengeoweb/workspace';
import geowebTranslations from './app/locales/geoweb.json';

export const GEOWEB_NAMESPACE = 'geoweb';

export const initGeowebI18n = (): void => {
  if (!i18n.isInitialized && !i18n.isInitializing) {
    void i18n.use(initReactI18next).init({
      lng: 'en',
      fallbackLng: 'en',
      defaultNS: GEOWEB_NAMESPACE,
      fallbackNS: [
        WORKSPACE_NAMESPACE,
        CAP_NAMESPACE,
        WARN_NAMESPACE,
        THEME_NAMESPACE,
        WEBMAP_REACT_NAMESPACE,
        WEBMAP_NAMESPACE,
        TIME_SERIES_NAMESPACE,
        SA_NAMESPACE,
        AUTH_NAMESPACE,
        CORE_NAMESPACE,
        SPACE_WEATHER_NAMESPACE,
        LAYER_SELECT_NAMESPACE,
        PLUGINS_NAMESPACE,
        SENTRY_NAMESPACE,
        SHARED_NAMESPACE,
        TAF_NAMESPACE,
        TIME_SLIDER_NAMESPACE,
        SNACKBAR_NAMESPACE,
        FORM_FIELDS_NAMESPACE,
        API_NAMESPACE,
        DATA_EXPLORER_NAMESPACE,
        GEOWEB_NAMESPACE,
        SOUNDINGS_NAMESPACE,
        KNMICOMPONENTS_NAMESPACE,
      ],
      interpolation: {
        escapeValue: false,
      },
      resources: {},
    });
  }

  loadAllTranslations(i18n, [
    { ns: WORKSPACE_NAMESPACE, translations: workspaceTranslations },
    { ns: WARN_NAMESPACE, translations: warningsTranslations },
    { ns: THEME_NAMESPACE, translations: themeTranslations },
    { ns: WEBMAP_REACT_NAMESPACE, translations: webmapReactTranslations },
    { ns: WEBMAP_NAMESPACE, translations: webmapTranslations },
    { ns: TIME_SERIES_NAMESPACE, translations: timeseriesTranslations },
    { ns: SA_NAMESPACE, translations: saTranslations },
    { ns: AUTH_NAMESPACE, translations: authTranslations },
    { ns: CORE_NAMESPACE, translations: coreTranslations },
    { ns: SPACE_WEATHER_NAMESPACE, translations: swTranslations },
    { ns: LAYER_SELECT_NAMESPACE, translations: layerSelectTranslations },
    { ns: PLUGINS_NAMESPACE, translations: pluginsTranslations },
    { ns: SENTRY_NAMESPACE, translations: sentryTranslations },
    { ns: SHARED_NAMESPACE, translations: sharedTranslations },
    { ns: TAF_NAMESPACE, translations: tafTranslations },
    { ns: TIME_SLIDER_NAMESPACE, translations: timesliderTranslations },
    { ns: SNACKBAR_NAMESPACE, translations: snackbarTranslations },
    { ns: CAP_NAMESPACE, translations: capTranslations },
    { ns: FORM_FIELDS_NAMESPACE, translations: formFieldsTranslations },
    { ns: API_NAMESPACE, translations: apiTranslations },
    { ns: DATA_EXPLORER_NAMESPACE, translations: dataExplorerTranslations },
    { ns: GEOWEB_NAMESPACE, translations: geowebTranslations },
    { ns: SOUNDINGS_NAMESPACE, translations: soundingsTranslations },
    { ns: KNMICOMPONENTS_NAMESPACE, translations: knmiComponentTranslations },
  ]);
};

export const useGeowebTranslation = (): UseTranslationResponse<
  typeof GEOWEB_NAMESPACE,
  typeof i18n
> => useTranslation(GEOWEB_NAMESPACE);

export const translateInTestsAndStories = (
  key: string,
  params: Record<string, string | number> | undefined = undefined,
): string =>
  i18n.t(key, {
    ns: [
      AUTH_NAMESPACE,
      FORM_FIELDS_NAMESPACE,
      GEOWEB_NAMESPACE,
      SPACE_WEATHER_NAMESPACE,
      WORKSPACE_NAMESPACE,
      SOUNDINGS_NAMESPACE,
    ],
    ...params,
  });

export default i18n;
